import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { CheckIcon, DashIcon } from "../icons/app";
import { cn } from "../lib/utils";
import { cva } from "class-variance-authority";
import React from "react";
import { Icon } from "./Icon";
import { Label } from "./Label";
import { Tooltip } from "./Tooltip";
export const CHECKBOX_SIZES = ["xs", "sm"];
const checkboxStyles = cva(cn("s-shrink-0 s-peer s-border s-transition s-duration-200 s-ease-in-out", "s-border-border-dark dark:s-border-border-dark-night s-bg-background dark:s-bg-background-night", "s-text-foreground dark:s-text-foreground-night", "focus-visible:s-ring-ring s-ring-offset-background focus-visible:s-outline-none focus-visible:s-ring-2 focus-visible:s-ring-offset-2", "hover:s-border-highlight hover:s-bg-highlight-50 dark:hover:s-bg-highlight-100-night hover:dark:s-border-highlight", "disabled:s-cursor-not-allowed disabled:s-opacity-50 disabled:s-border-border-dark disabled:dark:s-border-border-dark-night disabled:s-bg-background dark:disabled:s-bg-background-night"), {
    variants: {
        checked: {
            true: "data-[state=checked]:s-bg-primary dark:data-[state=checked]:s-bg-primary-night data-[state=checked]:s-text-white data-[state=checked]:s-border-primary",
            partial: "data-[state=indeterminate]:s-bg-primary dark:data-[state=indeterminate]:s-bg-primary-night data-[state=indeterminate]:s-text-white data-[state=indeterminate]:s-border-primary",
            false: "",
        },
        isMutedAfterCheck: {
            true: "",
            false: "",
        },
        size: {
            xs: "s-h-4 s-w-4 s-rounded",
            sm: "s-h-5 s-w-5 s-rounded-md",
        },
    },
    compoundVariants: [
        {
            checked: true,
            isMutedAfterCheck: true,
            className: "data-[state=checked]:s-bg-faint/50 data-[state=checked]:dark:s-bg-faint-night data-[state=checked]:s-border-transparent",
        },
    ],
    defaultVariants: {
        size: "sm",
        checked: false,
        isMutedAfterCheck: false,
    },
});
const Checkbox = React.forwardRef(({ className, size, checked, id, tooltip, isMutedAfterCheck, ...props }, ref) => {
    const checkbox = (React.createElement(CheckboxPrimitive.Root, { ref: ref, id: id, className: cn(checkboxStyles({ checked, size, isMutedAfterCheck }), className), checked: checked === "partial" ? "indeterminate" : checked, ...props },
        React.createElement(CheckboxPrimitive.Indicator, { className: "s-flex s-items-center s-justify-center s-text-current" },
            React.createElement("span", { className: cn(size === "xs" ? "-s-mt-px" : "") },
                React.createElement(Icon, { size: "xs", visual: checked === "partial" ? DashIcon : CheckIcon, className: "s-text-background dark:s-text-background-night" })))));
    return tooltip ? (React.createElement(Tooltip, { label: tooltip, trigger: checkbox, tooltipTriggerAsChild: true })) : (checkbox);
});
Checkbox.displayName = CheckboxPrimitive.Root.displayName;
function CheckboxWithText({ text, tooltip, id: idProp, size, ...props }) {
    // Unique id per instance so checkbox and label stay associated (htmlFor/id); required for a11y and click-label-to-toggle.
    const generatedId = React.useId();
    const id = idProp !== null && idProp !== void 0 ? idProp : generatedId;
    const content = (React.createElement("div", { className: "s-items-top s-flex s-items-center s-space-x-2" },
        React.createElement(Checkbox, { id: id, size: size, ...props }),
        React.createElement(Label, { htmlFor: id, className: cn("s-cursor-pointer s-leading-none peer-disabled:s-cursor-not-allowed peer-disabled:s-opacity-70", size === "xs" ? "s-text-xs" : "s-text-sm") }, text)));
    return tooltip ? React.createElement(Tooltip, { label: tooltip, trigger: content }) : content;
}
function CheckBoxWithTextAndDescription({ text, description, tooltip, id: idProp, size, ...props }) {
    // Unique id per instance so checkbox and label stay associated (htmlFor/id); required for a11y and click-label-to-toggle.
    const generatedId = React.useId();
    const id = idProp !== null && idProp !== void 0 ? idProp : generatedId;
    const content = (React.createElement("div", { className: "s-items-top s-flex s-space-x-2" },
        React.createElement(Checkbox, { id: id, size: size, ...props }),
        React.createElement("div", { className: "s-grid s-gap-1.5 s-leading-none" },
            React.createElement(Label, { htmlFor: id, className: cn("s-cursor-pointer s-leading-none peer-disabled:s-cursor-not-allowed peer-disabled:s-opacity-70", size === "xs" ? "s-text-xs" : "s-text-sm") }, text),
            React.createElement("p", { className: cn("s-text-xs", "s-text-muted-foreground dark:s-text-muted-foreground-night") }, description))));
    return tooltip ? React.createElement(Tooltip, { label: tooltip, trigger: content }) : content;
}
export { Checkbox, CheckboxWithText, CheckBoxWithTextAndDescription };
//# sourceMappingURL=Checkbox.js.map